import React, { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Trash2, Upload, X } from "lucide-react";
import { ClinicScheduleStatusDto } from "@/src/web-api-client";
import { Button } from "../../ui/button";
import { can, Perm } from "../../../auth/permissions";
import Can from "../../../auth/Can";
import Alert from "../../ui/Alert";

interface ClinicUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  clinic: ClinicScheduleStatusDto;
  onClinicUpdate: (
    file: File | null,
    data: ClinicForm,
    deleteCurrentImage: boolean
  ) => Promise<void>;
}

export const ClinicUpdateModal: React.FC<ClinicUpdateModalProps> = ({
  isOpen,
  onClose,
  clinic,
  onClinicUpdate,
}) => {
  const canManageClinic = can(Perm.Clinic_Manage);

  const {
    register,
    watch,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ClinicForm>({
    defaultValues: {
      description: clinic.description ?? "",
    },
  });

  const description = watch("description") ?? "";

  const [isDragOver, setIsDragOver] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [deleteCurrentImage, setDeleteCurrentImage] = useState(false);

  const hasCurrentServerImage = !!clinic.imageUrl;
  const showCurrentImage = hasCurrentServerImage && !previewImage && !deleteCurrentImage;
  const showDropzoneHint = !previewImage;

  const setSelectedImage = (selectedFile: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        setPreviewImage(event.target.result as string);
      }
    };
    reader.readAsDataURL(selectedFile);

    setFile(selectedFile);

    // Nếu đã chọn ảnh mới thì không còn là "xóa trắng ảnh cũ"
    setDeleteCurrentImage(false);
  };

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFile = files.find((f) => f.type.startsWith("image/"));

    if (imageFile) {
      setSelectedImage(imageFile);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];

      if (selectedFile && selectedFile.type.startsWith("image/")) {
        setSelectedImage(selectedFile);
      }

      e.target.value = "";
    },
    []
  );

  // Chỉ xóa preview ảnh mới chọn ở client
  const handleRemovePreview = () => {
    setPreviewImage(null);
    setFile(null);
  };

  // Đánh dấu xóa ảnh hiện tại trên server
  const handleDeleteCurrentImage = () => {
    setPreviewImage(null);
    setFile(null);
    setDeleteCurrentImage(true);
  };

  const onSubmit = handleSubmit(async (data: ClinicForm) => {
    if (!canManageClinic) return;

    await onClinicUpdate(file, data, deleteCurrentImage);

    setPreviewImage(null);
    setFile(null);
    setDeleteCurrentImage(false);
    onClose();
  });

  if (!isOpen) return null;

  return (
    <form
      className="fixed inset-0 top-[60px] z-50 flex items-center justify-center bg-black/50 p-4"
      onSubmit={onSubmit}
    >
      <div className="flex max-h-[90vh] w-fit max-w-[90%] flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b-2 bg-tempting-azure px-6 py-4">
          <h3 className="text-lg font-semibold text-[#646464]">
            {clinic.clinicName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-[#646464] hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-4">
          {!canManageClinic && (
            <Alert
              variant="destructive"
              msg="Bạn không có quyền cập nhật phòng khám"
            />
          )}

          <div className="flex flex-col items-stretch justify-center gap-6 flex-wrap lg:flex-row">
            {showCurrentImage && (
              <div className="w-full flex-1">
                <div className="relative">
                  <img
                    src={clinic.imageUrl!}
                    alt="Current"
                    className="h-64 w-full rounded-lg border border-gray-200 object-cover"
                  />

                  <button
                    type="button"
                    onClick={handleDeleteCurrentImage}
                    className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-red-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-600"
                  >
                    <Trash2 size={14} />
                    Xóa ảnh hiện tại
                  </button>
                </div>
              </div>
            )}

            {previewImage && (
              <div className="w-full flex-1">
                <div className="relative">
                  <img
                    src={previewImage}
                    alt="Preview"
                    className="h-64 w-full rounded-lg border border-gray-200 object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleRemovePreview}
                    className="absolute right-2 top-2 rounded-full bg-red-500 p-1.5 text-white transition-colors hover:bg-red-600"
                    title="Bỏ ảnh mới đã chọn"
                  >
                    <X size={12} />
                  </button>
                </div>
              </div>
            )}

            {deleteCurrentImage && !previewImage && (
              <div className="flex h-64 w-full flex-1 items-center justify-center rounded-lg border border-dashed border-red-200 bg-red-50">
                <div className="text-center">
                  <p className="text-sm font-semibold text-red-600">
                    Ảnh hiện tại sẽ bị xóa khi bấm Cập nhật
                  </p>
                  <p className="mt-1 text-xs text-red-500">
                    Bạn vẫn có thể chọn ảnh mới để thay thế.
                  </p>
                </div>
              </div>
            )}

            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`relative flex w-[400px] items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200 ${
                isDragOver
                  ? "border-blue-400 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
              }`}
            >
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleFileSelect}
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
              />

              <div className="flex flex-col items-center space-y-3">
                <div
                  className={`rounded-full p-3 transition-colors ${
                    isDragOver ? "bg-blue-100" : "bg-gray-100"
                  }`}
                >
                  <Upload
                    size={24}
                    className={isDragOver ? "text-blue-500" : "text-gray-500"}
                  />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Kéo thả ảnh vào đây
                  </p>
                  <p className="mt-1 text-xs text-gray-500">
                    Hoặc click để chọn file (PNG, JPG, JPEG, WEBP)
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 w-[600px] max-w-full">
            <label className="mb-2 block text-sm font-medium text-gray-700">
              Mô tả phòng khám
            </label>

            <textarea
              rows={6}
              placeholder="Ví dụ: Phòng Khám Tim mạch BV108 – siêu âm tim 4D, Holter 24h, khám nhanh…"
              className="mt-1 h-[200px] w-full rounded border border-input p-2 text-sm focus:outline-none focus:ring-1 focus:ring-green-600"
              {...register("description", {
                maxLength: {
                  value: 500,
                  message: "Vui lòng nhập mô tả ngắn ≤ 500 ký tự.",
                },
              })}
            />

            <div className="mt-1 text-xs text-gray-500">
              {description.length}/500 ký tự
            </div>

            {errors.description && (
              <p className="mt-1 text-sm text-red-500">
                {errors.description.message}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end border-t border-gray-200 px-6 py-4">
          <div className="flex space-x-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50"
            >
              Hủy
            </button>

            <Can perm={Perm.Clinic_Manage}>
              <Button
                type="submit"
                size="default"
                disabled={isSubmitting}
                className="bg-gradient-to-r from-primary to-primary-glow transition-colors duration-500 hover:from-primary-glow hover:to-primary disabled:opacity-50"
              >
                {isSubmitting ? "Đang cập nhật..." : "Cập nhật"}
              </Button>
            </Can>
          </div>
        </div>
      </div>
    </form>
  );
};